import { Injectable, inject } from '@angular/core';

import { FunnelSettingsProvider } from 'src/app/providers/funnel-settings';

/**
 * The funnel-entry shape the next-funnel resolvers inspect. The funnel config is
 * third-party shaped (TouchCR funnel config) and only loosely modelled by
 * `DefaultFunnel`; the resolvers touch only `type`, so the rest is `unknown`.
 * Mirrors the `defaultFunnels[]` element the component used to pass in.
 */
export interface FunnelCandidate {
  type?: string;
  [key: string]: unknown;
}

/**
 * The multi-upsell step resolver inspects only `step` and `type` on each funnel
 * entry. A loose mirror of `mainFunnel.defaultFunnels[]` as read by
 * `getNextStepForMultiUpsell`. `step` is always present on real funnel config,
 * so it is typed `number` to keep the sort/compare semantics identical to the
 * original (no defensive `?? 0` coercion that could shift behaviour).
 */
export interface MultiUpsellFunnelEntry {
  type?: string;
  step: number;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class UpsellFunnelNavigationService {
  private readonly funnelProvider = inject(FunnelSettingsProvider);

  /**
   * Resolves the next upsell funnel from a list of default funnels. Returns the
   * first entry whose `type` is (case-insensitively) `upsell`, or `undefined`
   * when the list is empty / has no upsell. Extracted verbatim from
   * `USGenericComponent.getNextUpsellFunnel`.
   */
  async getNextUpsellFunnel(defaultFunnels: FunnelCandidate[] = []): Promise<FunnelCandidate | undefined> {
    if (defaultFunnels.length === 0) return;
    const upsellFunnel = defaultFunnels.find((e) => e.type && e.type.toLowerCase() === 'upsell');
    if (!upsellFunnel) return;
    return upsellFunnel;
  }

  /**
   * Resolves the next downsell funnel from a list of default funnels. The first
   * entry is treated as a downsell only when it is missing a `type` or its
   * `type` is `downsell`; otherwise it falls back to the next upsell funnel.
   * Extracted verbatim from `USGenericComponent.getNextDownsellFunnel`.
   */
  async getNextDownsellFunnel(defaultFunnels: FunnelCandidate[] = []): Promise<FunnelCandidate | undefined> {
    if (defaultFunnels.length === 0) return;
    const downsellFunnel = defaultFunnels[0];
    if (!downsellFunnel || (downsellFunnel.type && downsellFunnel.type.toLowerCase() !== 'downsell')) {
      return await this.getNextUpsellFunnel(defaultFunnels);
    }
    return downsellFunnel;
  }

  /**
   * Gets the next step for multi-upsell navigation. Handles the case where
   * multiple products share the same step number.
   *
   * Pure decision: it reads the current funnel config (via
   * `funnelProvider.getMainFunnelByCurrentUrl()` — a read-only lookup), filters
   * to the navigable funnel types, sorts by step, and computes the next step.
   * It performs NO navigation and mutates NO component state, so it moves here
   * unchanged. Extracted verbatim from
   * `USGenericComponent.getNextStepForMultiUpsell`.
   *
   * @param currentStep - The current step number
   * @param isAccepted - Whether the upsell was accepted or declined
   * @returns The next step number
   */
  async getNextStepForMultiUpsell(currentStep: number, isAccepted: boolean = true): Promise<number> {
    const mainFunnel = this.funnelProvider.getMainFunnelByCurrentUrl();
    if (!mainFunnel) return currentStep + 1;

    const defaultFunnelList = (mainFunnel.defaultFunnels as MultiUpsellFunnelEntry[])
      .filter(
        (item) =>
          item.type === 'Order Form' ||
          item.type === 'Thankyou Page' ||
          item.type === 'Upsell' ||
          item.type === 'DownSell',
      )
      .sort((a, b) => a.step - b.step);

    if (isAccepted) {
      // For accepted upsells, go to the next step in sequence
      const nextStep = defaultFunnelList.find((funnel) => funnel.step > currentStep + 1);
      return nextStep ? nextStep.step : currentStep + 1;
    } else {
      // For declined upsells, skip to the next non-upsell step
      const nextNonUpsellStep = defaultFunnelList.find(
        (funnel) =>
          funnel.step > currentStep &&
          (funnel.type === 'DownSell' || funnel.type === 'Thankyou Page' || funnel.type === 'Order Form'),
      );
      return nextNonUpsellStep ? nextNonUpsellStep.step : currentStep + 1;
    }
  }
}
